import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type { LanguageDto, SelfUpdateRequest, UserCreateRequest, UserUpdateRequest } from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { useLanguages } from "@/helpers/languages";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";
import type { ApiResponseType } from "@/types/api-types";

const useAddUser = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: UserCreateRequest) => api.postUsersV1(payload).then((x) => x.data),
    onSuccess: () => {
      showFlashToast({ type: "success", title: t("page.create-user.new.success") });
    },
    onError: () => {
      showFlashToast({ type: "error", title: t("page.create-user.new.error") });
    },
  });
};

const useUpdateSelf = (opts: { onError: (error: unknown) => void; onSuccess: () => void }) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (payload: SelfUpdateRequest) => api.putSelfV3(payload),
    onSettled() {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SELF(projectId) });
    },
    ...opts,
  });
};

const useUpdateUser = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const sessionUser = useSessionUser();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: UserUpdateRequest }) =>
      api.putUsersV1(userId, data).then((x) => x.data),
    onSuccess: (_, payload) => {
      showFlashToast({ type: "success", title: t("page.create-user.edit.success") });
      if (sessionUser.id === payload.userId) {
        void queryClient.invalidateQueries();
      } else {
        void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.USER_DETAILS(projectId, payload.userId) });
      }
    },
    onError: () => {
      showFlashToast({ type: "error", title: t("page.create-user.edit.error") });
    },
  });
};

const useDeleteUser = ({ onSuccess }: { onSuccess?: () => void }) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ userId }: { userId: string }) => api.deleteUsersByIdV1(userId),
    onSuccess: (_, payload) => {
      showFlashToast({ type: "success", title: t("page.user-detail.delete.success") });

      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.USER_DETAILS(projectId, payload.userId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.USER_POSTS(projectId, payload.userId) });

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      showFlashToast({ type: "error", title: t("page.user-detail.delete.error") });
    },
  });
};

const useAddUserSignUpReminders = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ userId }: { userId: string }) => api.postUsersUserSignupRemindersV1(userId),
    onSuccess: async (_, payload) => {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.USER_SIGNUP_REMINDERS(projectId, payload.userId) });
      showFlashToast({ type: "success", title: t("page.user-detail.invite-user.success") });
    },
    onError() {
      showFlashToast({ type: "error", title: t("page.user-detail.invite-user.error") });
    },
  });
};

const useCheckEmailExists = () => {
  const api = useApi();

  return useMutation({
    mutationFn: (email: string) => api.postUsersEmailExistsV1({ email }).then((x) => x.data),
  });
};

const useMarkCodeOfConductAsAgreed = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: () => api.postSelfCodeOfConductAgreeV1(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SELF(projectId) });
    },
    onError() {
      showFlashToast({ type: "error", title: t("page.onboarding.code-of-conduct.agree.notification.failed") });
    },
  });
};

const useUpdateSelfLanguage = () => {
  const projectId = useProjectId();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const api = useApi();
  const showFlashToast = useFlashToast();
  const languages = useLanguages();

  return useMutation({
    mutationFn: (languageId: LanguageDto["id"]) =>
      api
        .putSelfLanguageV1({
          languageId,
        })
        .then(({ data }) => data),
    onMutate: async (newLanguageId) => {
      const newLanguage = languages.data?.find((language) => language.id === newLanguageId);

      if (newLanguage) {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries({
          queryKey: QUERY_KEYS.SELF(projectId),
        });

        // Snapshot the previous value
        const previousQueryData = queryClient.getQueryData(QUERY_KEYS.SELF(projectId));

        // Optimistically update the cache with the new language
        queryClient.setQueryData<ApiResponseType<"getSelfV2"> | undefined>(
          QUERY_KEYS.SELF(projectId),
          (oldResponse) => {
            if (oldResponse) {
              return {
                ...oldResponse,
                data: {
                  ...oldResponse.data,
                  language: newLanguage,
                },
              };
            }
          },
        );

        return {
          previousQueryData,
        };
      }
    },
    onError: (_, __, context) => {
      showFlashToast({
        type: "error",
        title: t("model.self.action.change-language.notification.error"),
      });
      queryClient.setQueryData(QUERY_KEYS.SELF(projectId), context?.previousQueryData);
    },
  });
};

export const userMutations = {
  useUpdateSelf,
  useUpdateSelfLanguage,
  useAddUserSignUpReminders,
  useAddUser,
  useUpdateUser,
  useDeleteUser,
  useCheckEmailExists,
  useMarkCodeOfConductAsAgreed,
};
